import { useCastersContext } from "@/pages/caster/caster-data-entry-v2/CastersContext";
import { NumberTextFieldDE } from "@/pages/caster/caster-data-entry-v2/components/form-fields/common/NumberTextFieldDE";
import { useSaveStrand } from "@/pages/caster/caster-data-entry-v2/hooks/useSaveStrand";
import { StrandData } from "@/pages/caster/models/StrandData";
import { ButtonWithLoading, MesfModal } from "@dexteel/mesf-core";
import { Button, Divider, Grid, Typography } from "@material-ui/core";
import React, { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { formatNumberToDecimals } from "../helpers/helpers";

interface EditStrandModalProps {
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
	strandNum: number | null;
}

type FormValues = Pick<
	StrandData,
	| "MWTempInStart"
	| "MWTempInEnd"
	| "MWDTStart"
	| "MWDTEnd"
	| "BilletTempStart"
	| "BilletTempEnd"
	| "NSprayStart"
	| "NSprayEnd"
	| "ESprayStart"
	| "ESprayEnd"
	| "SSprayStart"
	| "SSprayEnd"
	| "WSprayStart"
	| "WSprayEnd"
	| "SpeedStart"
	| "SpeedEnd"
>;

const fieldMaxDecimals: { [K in keyof FormValues]: number } = {
	MWTempInStart: 1,
	MWTempInEnd: 1,
	MWDTStart: 1,
	MWDTEnd: 1,
	BilletTempStart: 0,
	BilletTempEnd: 0,
	NSprayStart: 0,
	NSprayEnd: 0,
	ESprayStart: 0,
	ESprayEnd: 0,
	SSprayStart: 0,
	SSprayEnd: 0,
	WSprayStart: 0,
	WSprayEnd: 0,
	SpeedStart: 2,
	SpeedEnd: 2,
};

export const EditStrandModal: React.FC<EditStrandModalProps> = ({
	show,
	onHide,
	strandNum,
}) => {
	const { state, actions } = useCastersContext();
	const { save: saveStrand } = useSaveStrand();
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);

	const strands: StrandData[] = state.strands || [];
	const strand = strands.find((s) => s.Strand === strandNum);

	const { control, handleSubmit, reset } = useForm<FormValues>({
		defaultValues: {
			MWTempInStart: null,
			MWTempInEnd: null,
			MWDTStart: null,
			MWDTEnd: null,
			NSprayStart: null,
			NSprayEnd: null,
			ESprayStart: null,
			ESprayEnd: null,
			SSprayStart: null,
			SSprayEnd: null,
			WSprayStart: null,
			WSprayEnd: null,
			SpeedStart: null,
			SpeedEnd: null,
		},
	});

	useEffect(() => {
		if (show && strand) {
			reset({
				MWTempInStart: formatNumberToDecimals(
					strand.MWTempInStart,
					fieldMaxDecimals.MWTempInStart,
				),
				MWTempInEnd: formatNumberToDecimals(
					strand.MWTempInEnd,
					fieldMaxDecimals.MWTempInEnd,
				),
				MWDTStart: formatNumberToDecimals(
					strand.MWDTStart,
					fieldMaxDecimals.MWDTStart,
				),
				MWDTEnd: formatNumberToDecimals(
					strand.MWDTEnd,
					fieldMaxDecimals.MWDTEnd,
				),
				BilletTempStart: formatNumberToDecimals(
					strand.BilletTempStart,
					fieldMaxDecimals.BilletTempStart,
				),
				BilletTempEnd: formatNumberToDecimals(
					strand.BilletTempEnd,
					fieldMaxDecimals.BilletTempEnd,
				),
				NSprayStart: formatNumberToDecimals(
					strand.NSprayStart,
					fieldMaxDecimals.NSprayStart,
				),
				NSprayEnd: formatNumberToDecimals(
					strand.NSprayEnd,
					fieldMaxDecimals.NSprayEnd,
				),
				ESprayStart: formatNumberToDecimals(
					strand.ESprayStart,
					fieldMaxDecimals.ESprayStart,
				),
				ESprayEnd: formatNumberToDecimals(
					strand.ESprayEnd,
					fieldMaxDecimals.ESprayEnd,
				),
				SSprayStart: formatNumberToDecimals(
					strand.SSprayStart,
					fieldMaxDecimals.SSprayStart,
				),
				SSprayEnd: formatNumberToDecimals(
					strand.SSprayEnd,
					fieldMaxDecimals.SSprayEnd,
				),
				WSprayStart: formatNumberToDecimals(
					strand.WSprayStart,
					fieldMaxDecimals.WSprayStart,
				),
				WSprayEnd: formatNumberToDecimals(
					strand.WSprayEnd,
					fieldMaxDecimals.WSprayEnd,
				),
				SpeedStart: formatNumberToDecimals(
					strand.SpeedStart,
					fieldMaxDecimals.SpeedStart,
				),
				SpeedEnd: formatNumberToDecimals(
					strand.SpeedEnd,
					fieldMaxDecimals.SpeedEnd,
				),
			});
		}
	}, [show, reset, strand]);

	const onSubmit: SubmitHandler<FormValues> = async (data) => {
		if (!strand || strandNum === null) return;

		setIsSubmitLoading(true);

		const savePromises = Object.entries(data).map(([field, value]) => {
			if (strand[field as keyof StrandData] !== value) {
				return saveStrand(strandNum, field, value);
			}
			return Promise.resolve();
		});

		await Promise.all(savePromises);

		setIsSubmitLoading(false);
		if (!state.errorMessage) {
			onHide(true);
		}
	};

	return (
		<MesfModal
			title={`Edit Strand ${strandNum}`}
			open={show}
			handleClose={() => {
				onHide(false);
			}}
			id="edit-strand-values-modal"
			maxWidth="md"
		>
			<form onSubmit={handleSubmit(onSubmit)}>
				<MesfModal.Content dividers style={{ padding: "15px 30px" }}>
					<Grid container spacing={3}>
						{/* --- Top Section: Mold Water (Full Width) --- */}
						<Grid item xs={12}>
							<Typography variant="h6" align="center" gutterBottom>
								Mold Water
							</Typography>
							<Grid container spacing={2}>
								{/* MW Fields (4 inline) */}
								<Grid item xs={6} sm={3}>
									<Controller
										name="MWTempInStart"
										control={control}
										render={({ field }) => (
											<NumberTextFieldDE
												{...field}
												value={field.value}
												onChange={field.onChange}
												onBlur={() => {}}
												label="Temp In Start"
												pause={actions.setRefreshInPause.bind(null, true)}
												resume={actions.setRefreshInPause.bind(null, false)}
												maxDecimals={fieldMaxDecimals.MWTempInStart}
											/>
										)}
									/>
								</Grid>
								<Grid item xs={6} sm={3}>
									<Controller
										name="MWTempInEnd"
										control={control}
										render={({ field }) => (
											<NumberTextFieldDE
												{...field}
												value={field.value}
												onChange={field.onChange}
												onBlur={() => {}}
												label="Temp In End"
												pause={actions.setRefreshInPause.bind(null, true)}
												resume={actions.setRefreshInPause.bind(null, false)}
												maxDecimals={fieldMaxDecimals.MWTempInEnd}
											/>
										)}
									/>
								</Grid>
								<Grid item xs={6} sm={3}>
									<Controller
										name="MWDTStart"
										control={control}
										render={({ field }) => (
											<NumberTextFieldDE
												{...field}
												value={field.value}
												onChange={field.onChange}
												onBlur={() => {}}
												label="DT Start"
												pause={actions.setRefreshInPause.bind(null, true)}
												resume={actions.setRefreshInPause.bind(null, false)}
												maxDecimals={fieldMaxDecimals.MWDTStart}
											/>
										)}
									/>
								</Grid>
								<Grid item xs={6} sm={3}>
									<Controller
										name="MWDTEnd"
										control={control}
										render={({ field }) => (
											<NumberTextFieldDE
												{...field}
												value={field.value}
												onChange={field.onChange}
												onBlur={() => {}}
												label="DT End"
												pause={actions.setRefreshInPause.bind(null, true)}
												resume={actions.setRefreshInPause.bind(null, false)}
												maxDecimals={fieldMaxDecimals.MWDTEnd}
											/>
										)}
									/>
								</Grid>
							</Grid>
						</Grid>

						{/* --- Divider --- */}
						<Grid item xs={12} style={{ margin: "4px 0" }}>
							<Divider />
						</Grid>

						{/* --- Middle Section: Spray Pressure --- */}
						<Grid item xs={12}>
							<Typography variant="h6" align="center" gutterBottom>
								Spray Pressure
							</Typography>
							<Grid container spacing={2}>
								{/* Row 1: N Start, N End, S Start, S End */}
								<Grid item xs={6} sm={3}>
									<Controller
										name="NSprayStart"
										control={control}
										render={({ field }) => (
											<NumberTextFieldDE
												{...field}
												value={field.value}
												onChange={field.onChange}
												onBlur={() => {}}
												label="N Start"
												pause={actions.setRefreshInPause.bind(null, true)}
												resume={actions.setRefreshInPause.bind(null, false)}
												maxDecimals={fieldMaxDecimals.NSprayStart}
											/>
										)}
									/>
								</Grid>
								<Grid item xs={6} sm={3}>
									<Controller
										name="NSprayEnd"
										control={control}
										render={({ field }) => (
											<NumberTextFieldDE
												{...field}
												value={field.value}
												onChange={field.onChange}
												onBlur={() => {}}
												label="N End"
												pause={actions.setRefreshInPause.bind(null, true)}
												resume={actions.setRefreshInPause.bind(null, false)}
												maxDecimals={fieldMaxDecimals.NSprayEnd}
											/>
										)}
									/>
								</Grid>
								<Grid item xs={6} sm={3}>
									<Controller
										name="SSprayStart"
										control={control}
										render={({ field }) => (
											<NumberTextFieldDE
												{...field}
												value={field.value}
												onChange={field.onChange}
												onBlur={() => {}}
												label="S Start"
												pause={actions.setRefreshInPause.bind(null, true)}
												resume={actions.setRefreshInPause.bind(null, false)}
												maxDecimals={fieldMaxDecimals.SSprayStart}
											/>
										)}
									/>
								</Grid>
								<Grid item xs={6} sm={3}>
									<Controller
										name="SSprayEnd"
										control={control}
										render={({ field }) => (
											<NumberTextFieldDE
												{...field}
												value={field.value}
												onChange={field.onChange}
												onBlur={() => {}}
												label="S End"
												pause={actions.setRefreshInPause.bind(null, true)}
												resume={actions.setRefreshInPause.bind(null, false)}
												maxDecimals={fieldMaxDecimals.SSprayEnd}
											/>
										)}
									/>
								</Grid>
								{/* Row 2: E Start, E End, W Start, W End */}
								<Grid item xs={6} sm={3}>
									<Controller
										name="ESprayStart"
										control={control}
										render={({ field }) => (
											<NumberTextFieldDE
												{...field}
												value={field.value}
												onChange={field.onChange}
												onBlur={() => {}}
												label="E Start"
												pause={actions.setRefreshInPause.bind(null, true)}
												resume={actions.setRefreshInPause.bind(null, false)}
												maxDecimals={fieldMaxDecimals.ESprayStart}
											/>
										)}
									/>
								</Grid>
								<Grid item xs={6} sm={3}>
									<Controller
										name="ESprayEnd"
										control={control}
										render={({ field }) => (
											<NumberTextFieldDE
												{...field}
												value={field.value}
												onChange={field.onChange}
												onBlur={() => {}}
												label="E End"
												pause={actions.setRefreshInPause.bind(null, true)}
												resume={actions.setRefreshInPause.bind(null, false)}
												maxDecimals={fieldMaxDecimals.ESprayEnd}
											/>
										)}
									/>
								</Grid>
								<Grid item xs={6} sm={3}>
									<Controller
										name="WSprayStart"
										control={control}
										render={({ field }) => (
											<NumberTextFieldDE
												{...field}
												value={field.value}
												onChange={field.onChange}
												onBlur={() => {}}
												label="W Start"
												pause={actions.setRefreshInPause.bind(null, true)}
												resume={actions.setRefreshInPause.bind(null, false)}
												maxDecimals={fieldMaxDecimals.WSprayStart}
											/>
										)}
									/>
								</Grid>
								<Grid item xs={6} sm={3}>
									<Controller
										name="WSprayEnd"
										control={control}
										render={({ field }) => (
											<NumberTextFieldDE
												{...field}
												value={field.value}
												onChange={field.onChange}
												onBlur={() => {}}
												label="W End"
												pause={actions.setRefreshInPause.bind(null, true)}
												resume={actions.setRefreshInPause.bind(null, false)}
												maxDecimals={fieldMaxDecimals.WSprayEnd}
											/>
										)}
									/>
								</Grid>
							</Grid>
						</Grid>

						{/* --- Divider --- */}
						<Grid item xs={12} style={{ margin: "4px" }}>
							<Divider />
						</Grid>

						{/* --- Bottom Row: Billet (Left) & Speed (Right) */}
						<Grid item xs={12}>
							<Grid
								container
								spacing={2}
								alignItems="flex-start"
								justifyContent="center"
							>
								{/* Left Side: Billet */}
								<Grid item xs={12} md={6}>
									<Typography variant="h6" align="center" gutterBottom>
										Billet
									</Typography>
									<Grid container spacing={2}>
										<Grid item xs={12} sm={6}>
											<Controller
												name="BilletTempStart"
												control={control}
												render={({ field }) => (
													<NumberTextFieldDE
														{...field}
														value={field.value}
														onChange={field.onChange}
														onBlur={() => {}}
														label="Temp Start"
														pause={actions.setRefreshInPause.bind(null, true)}
														resume={actions.setRefreshInPause.bind(null, false)}
														maxDecimals={fieldMaxDecimals.BilletTempStart}
													/>
												)}
											/>
										</Grid>
										<Grid item xs={12} sm={6}>
											<Controller
												name="BilletTempEnd"
												control={control}
												render={({ field }) => (
													<NumberTextFieldDE
														{...field}
														value={field.value}
														onChange={field.onChange}
														onBlur={() => {}}
														label="Temp End"
														pause={actions.setRefreshInPause.bind(null, true)}
														resume={actions.setRefreshInPause.bind(null, false)}
														maxDecimals={fieldMaxDecimals.BilletTempEnd}
													/>
												)}
											/>
										</Grid>
									</Grid>
								</Grid>

								{/* Right Side: Speed */}
								<Grid item xs={12} md={6}>
									<Typography variant="h6" align="center" gutterBottom>
										Speed
									</Typography>
									<Grid container spacing={2}>
										<Grid item xs={12} sm={6}>
											<Controller
												name="SpeedStart"
												control={control}
												render={({ field }) => (
													<NumberTextFieldDE
														{...field}
														value={field.value}
														onChange={field.onChange}
														onBlur={() => {}}
														label="Start"
														pause={actions.setRefreshInPause.bind(null, true)}
														resume={actions.setRefreshInPause.bind(null, false)}
														maxDecimals={fieldMaxDecimals.SpeedStart}
													/>
												)}
											/>
										</Grid>
										<Grid item xs={12} sm={6}>
											<Controller
												name="SpeedEnd"
												control={control}
												render={({ field }) => (
													<NumberTextFieldDE
														{...field}
														value={field.value}
														onChange={field.onChange}
														onBlur={() => {}}
														label="End"
														pause={actions.setRefreshInPause.bind(null, true)}
														resume={actions.setRefreshInPause.bind(null, false)}
														maxDecimals={fieldMaxDecimals.SpeedEnd}
													/>
												)}
											/>
										</Grid>
									</Grid>
								</Grid>
							</Grid>
						</Grid>
					</Grid>

					{state.errorMessage && (
						<Typography color="error" style={{ marginTop: "10px" }}>
							{state.errorMessage}
						</Typography>
					)}
				</MesfModal.Content>
				<MesfModal.Actions style={{ padding: "20px" }}>
					<Grid container spacing={2} justifyContent="flex-end">
						<Grid item xs={12} md={3}>
							<Button
								fullWidth
								variant="contained"
								color="default"
								onClick={() => {
									onHide(false);
								}}
							>
								Cancel
							</Button>
						</Grid>
						<Grid item xs={12} md={3}>
							<ButtonWithLoading
								fullWidth
								isLoading={isSubmitLoading}
								variant="contained"
								color="primary"
								type="submit"
							>
								Save
							</ButtonWithLoading>
						</Grid>
					</Grid>
				</MesfModal.Actions>
			</form>
		</MesfModal>
	);
};
